<template>
  <div class="header" :style="bgc">
    <b-col>
      <b-row class="topheader-container">
        <h5 class="application-sub-heading">
          <p v-if="applicationSubHeading && applicationSubHeading.length > 0" class="applicationSubHeading">
            {{ applicationSubHeading }}
          </p>
        </h5>
        <div class="topheader">
          <div id="logo" class="top-header-left-flex">
            <a :href="config.authUrl" class="d-flex">
              <img :src="logoSrc" alt="MMS Logo" />
            </a>
            <h1 class="env-text">{{ environment }}</h1>
          </div>
          <div class="top-header-middle">
            <div class="text-white webtools-horizontal">
              <app-nav-item
                v-for="(app, index) in appsWithAccess"
                :key="app.id"
                :app-id="app.id"
                :app-data="app"
                :is-current-app="currentApp === app.id"
                :app-index="index"
                :progress-offset="progressOffset"
                :app-link="getAppLink(app.configLinkKey)"
                :icon-src="app.iconSrc"
                :header-background-color="config.backgroundColor"
                :show-info-icon="app.showInfoIcon"
                @start-hover="startHover"
                @stop-hover="stopHover"
              />
            </div>
          </div>
          <div class="top-header-right-flex contextmenu-container">
            <div class="webtools-dropdown-container">
              <b-dropdown id="dropdown-2" text="More tools" class="m-2 webtools-dropdown">
                <template v-for="app in appsWithAccess.filter(a => a.id !== 'portal')">
                  <app-dropdown-item
                    :key="app.id + '-dropdown'"
                    :is-current-app="currentApp === app.id"
                    :icon-src="currentApp === app.id ? app.iconSrc : app.iconSrcBlue"
                    :label="app.title"
                    :app-link="getAppLink(app.configLinkKey)"
                  />
                </template>
                <b-dropdown-divider></b-dropdown-divider>
                <template v-for="app in appsWithAccess.filter(a => a.id === 'portal')">
                  <app-dropdown-item
                    :key="app.id + '-dropdown'"
                    :is-current-app="currentApp === app.id"
                    :icon-src="currentApp === app.id ? app.iconSrc : app.iconSrcBlue"
                    :label="app.title"
                    :app-link="getAppLink(app.configLinkKey)"
                  />
                </template>
              </b-dropdown>
            </div>
            <b-dropdown id="dropdown-1" split :text="user" class="contextmenu-login">
              <b-dropdown-item @click="logout">Log out</b-dropdown-item>
              <b-dropdown-item @click="onClickClear">Clear Cache</b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </b-row>
    </b-col>
  </div>
</template>

<script>
import AppNavItem from './AppNavItem.vue';
import AppDropdownItem from './AppDropdownItem.vue';
import { createAppsConfig } from '../../config/appsConfig.js';
import logoSrc from '../../assets/images/mms_logo_white.svg';

export default {
  name: 'PageHeader',
  components: {
    AppNavItem,
    AppDropdownItem
  },
  props: {
    currentApp: {
      type: String,
      required: true,
      validator(value) {
        return ['broadcast', 'online-video', 'cross-platform', 'video-in-home', 'portal'].includes(value);
      }
    },
    config: {
      type: Object,
      required: true
    },
    user: {
      type: String,
      default: ''
    },
    appAccess: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      apps: createAppsConfig(),
      logoSrc,
      infoCardPreDelay: 400,
      infoCardDelay: 800,
      infoCardPostDelay: 200,
      hoveredIndex: null,
      infoVisibleIndex: null,
      hoverProgress: 0,
      hoverDelayTimer: null,
      progressInterval: null,
      postDelayTimer: null,
      prefetchedUrls: new Set()
    };
  },
  mounted() {
    this.prefetchIdleApps();
  },
  computed: {
    appsWithAccess() {
      return this.apps.map(app => ({
        ...app,
        userAccess: this.appAccess[app.id] !== undefined ? this.appAccess[app.id] : app.userAccess
      }));
    },
    applicationSubHeading() {
      return this.currentApp === 'video-in-home' ? this.config.subHeadingVideoInHome : this.config.subHeading;
    },
    progressOffset() {
      const circumference = 2 * Math.PI * 10;
      return circumference - (this.hoverProgress / 100) * circumference;
    },
    bgc() {
      return { 'background-color': this.config.backgroundColor };
    },
    environment() {
      const heading = (this.config.heading || '').toLowerCase();
      if (heading.includes(' dev')) return 'DEV';
      if (heading.includes(' test')) return 'TEST';
      if (heading.includes(' local')) return 'LOCAL';
      return '';
    }
  },
  methods: {
    getAppLink(appKey) {
      if (!this.config.appLinks || !this.config.appLinks[appKey]) return '#';
      return this.config.appLinks[appKey];
    },
    prefetchUrl(href) {
      if (!href || href === '#' || this.prefetchedUrls.has(href)) return;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      document.head.appendChild(link);
      this.prefetchedUrls.add(href);
    },
    prefetchIdleApps() {
      const prefetch = () => {
        this.appsWithAccess.forEach(app => {
          if (app.id !== this.currentApp && app.userAccess) {
            this.prefetchUrl(this.getAppLink(app.configLinkKey));
          }
        });
      };
      if (window.requestIdleCallback) {
        window.requestIdleCallback(prefetch);
      } else {
        setTimeout(prefetch, 2000);
      }
    },
    startHover(index) {
      if (this.hoveredIndex === index) return;
      const app = this.appsWithAccess[index];
      if (app && app.id !== this.currentApp && app.userAccess) {
        this.prefetchUrl(this.getAppLink(app.configLinkKey));
      }
      this.stopHover();
      this.hoveredIndex = index;
      this.hoverDelayTimer = setTimeout(() => {
        this.apps[index].showInfoIcon = true;
        this.infoVisibleIndex = index;
        this.startProgress(index);
      }, this.infoCardPreDelay);
    },
    startProgress(index) {
      this.hoverProgress = 0;
      const increments = 20;
      const stepTime = this.infoCardDelay / increments;
      this.progressInterval = setInterval(() => {
        if (this.hoverProgress < 100) {
          this.hoverProgress += 5;
        } else {
          clearInterval(this.progressInterval);
          this.progressInterval = null;
          this.postDelayTimer = setTimeout(() => {
            if (this.hoveredIndex === index) {
              this.apps[index].popupVisible = true;
            }
          }, this.infoCardPostDelay);
          setTimeout(() => (this.apps[index].showInfoIcon = false), 100);
        }
      }, stepTime);
    },
    stopHover() {
      clearTimeout(this.hoverDelayTimer);
      clearInterval(this.progressInterval);
      clearTimeout(this.postDelayTimer);
      this.hoveredIndex = null;
      this.infoVisibleIndex = null;
      this.hoverDelayTimer = null;
      this.progressInterval = null;
      this.postDelayTimer = null;
      this.hoverProgress = 0;
      this.apps.forEach(app => { app.popupVisible = false; app.showInfoIcon = false; });
    },
    logout() {
      this.$emit('logout');
    },
    onClickClear() {
      const accessToken = localStorage.getItem('accessToken');
      const user = localStorage.getItem('user');
      localStorage.clear();
      if (accessToken) localStorage.setItem('accessToken', accessToken);
      if (user) localStorage.setItem('user', user);
      location.reload();
    }
  }
};
</script>

<style scoped>
>>> .header { background-color: #337ab7; height: 70px; position: relative; }
>>> .topheader-container { height: 70px; padding: 0 20px; align-content: center; }
>>> .topheader { position: relative; width: 100%; display: flex; justify-content: space-between; height: 100%; }
>>> .application-sub-heading { position: absolute; top: 52px; }
>>> .top-header-left-flex { width: 25%; display: flex; justify-content: flex-start; align-content: center; }
>>> .top-header-right-flex { width: 25%; display: flex; justify-content: flex-end; }
>>> .top-header-middle { width: 50%; height: 100%; display: flex; justify-content: center; align-items: center; }
>>> .top-header-left-flex img { width: 8em; }
>>> .applicationSubHeading { font-size: small; color: white !important; -webkit-text-stroke: 0.04em red; font-weight: bold; }
>>> .env-text { color: white; padding: 10px 0 0 0; margin-left: 1rem; }
>>> .contextmenu-login > button:first-child:hover { border-color: #6c757d; cursor: default; }
>>> .top-header-right-flex button { background-color: inherit !important; }
>>> #dropdown-1 { max-height: 40px; align-self: center; }
>>> .webtools-dropdown-container { border: none !important; margin-top: 7px !important; padding: 0; }
>>> .webtools-dropdown button { border: none !important; border-color: transparent !important; padding-left: 1rem; }
>>> .webtools-dropdown ul { font-size: 0.8rem; }
>>> .webtool-icon-dropdown { width: 24px; height: 24px; margin-right: 0.8rem; margin-bottom: 0.15rem; object-fit: contain; }
>>> #dropdown-2 { display: none; }
>>> .webtools-horizontal { display: flex; justify-content: center; align-items: center; padding: 0; margin: 0; height: 100%; width: 100%; max-width: 1100px; }
@media (max-width: 1099px) {
  >>> #dropdown-2 { display: block; }
  >>> .webtools-horizontal { display: none; }
}
</style>
